import { useEffect, useState, useCallback, useMemo } from 'react';
import { ALL_EXCLUDED, ALL_EXCLUDED_AO3, ALL_EXCLUDED_RR, SOURCE } from '../../../core/constants';
import {
  BlockAuthor,
  Delete,
  Return,
  ToCRM,
  Watch,
  ToPodiumTitle
} from '../../../composed-components/Actions';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { BASEFONTSIZE, COLUMNSHEADING, PAGESIZE, REFETCHDELAY, RefetchOptions, SOURCE_FORMAT } from '../constants';
import { EFontSize, TColumns } from '../types/types';
import { Sorting, FilterForm, Settings, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import Pagination from '../../../components/Pagination';
import getAllColumns from '../columns/getAllColumns';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import { TableFooter } from '../../../components/Table';
import Legend from '../../../composed-components/Legend';
import { useActions } from '../../../context/Actions.context';
import getDataSource from '../utils/getDataSource';

const AllExcluded = () => {
  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('All Titles');

  const { filter } = useFilter();
  const { sort } = useSort();
  const { source: sourcePath, isActionLoading, setActionLoading, isRefreshed } = useActions();

  const deleteBooks = book => setDeletedItems(book);
  const changePage = p => setPage(p);
  const changeFontSize = size => setFontSize(size);

  const SOURCE_TO_EXCLUDED: Record<string, string> = {
    [SOURCE.bookstat]: ALL_EXCLUDED,
    [SOURCE['royal-road']]: ALL_EXCLUDED_RR,
    [SOURCE.ao3]: ALL_EXCLUDED_AO3
  };
  const TAB_ID = SOURCE_TO_EXCLUDED[sourcePath['source']] ?? ALL_EXCLUDED;

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    TAB_ID, {
      page,
      ...filter[TAB_ID],
      ...sort[TAB_ID]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(TAB_ID, { ...filter[TAB_ID] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortColumns = useCallback(
    (title: string) => (
      <Sorting id={TAB_ID} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const allColumns = getAllColumns(sortColumns);
    const head = COLUMNSHEADING[TAB_ID].map(title => allColumns[title]);

    if (edit_titles) {
      head.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <Delete book={book} id={TAB_ID} />
            <BlockAuthor book={book} id={TAB_ID} />
            <ToPodiumTitle book={book} id={TAB_ID} />
            <Watch book={book} id={TAB_ID} />
            { move_titles_hubspot && <ToCRM book={book} id={TAB_ID} /> }
            <Return book={book} id={TAB_ID} />
          </div>
        )
      });
    }

    return head;
  }, [edit_titles, move_titles_hubspot, COLUMNSHEADING, sortColumns]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data, selectedOption, SOURCE_FORMAT[sourcePath['source']]);

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
    return () => {
      setSelectedOption('All Titles');
    };
  }, [page, filter[TAB_ID], sort[TAB_ID]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TAB_ID}
          page={page}
          changePage={changePage}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
        <Settings
          thead={thead}
          id={TAB_ID}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deletedItems}
        />
      </div>

      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            dataSource={dataSource}
            deleteItems={deletedItems}
            isFetching={isFetching || isActionLoading}
            id={TAB_ID}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter onlyPagination={TAB_ID === ALL_EXCLUDED_AO3}>
            {TAB_ID !== ALL_EXCLUDED_AO3 &&
              (<Legend map={['crm', 'podium']} />)}
            <Pagination
              total={count}
              current={page}
              onChange={changePage}
              showSizeChanger={false}
              pageSize={PAGESIZE}
              showQuickJumper={true}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default AllExcluded;